import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { ChevronDown, ChevronRight, RefreshCw, Save, ShieldCheck, Square, CheckSquare, ShieldPlus } from 'lucide-react';
import {
  menuService,
  roleService,
  roleMenuService,
  permissionService,
  rolePermissionService,
  type Menu,
  type Permission,
  type Role,
  type RoleMenu,
  type RoleMenuCreate,
  type RolePermission,
  type RolePermissionCreate,
} from '@kss/api';
import { refreshDynamicMenu } from '../../hooks/useDynamicMenu';

/**
 * Role → Menu visibility matrix.
 *
 * Pick a role from the dropdown; the tree below renders every Menu in
 * the tenant with a checkbox for "this role can see this menu". Save
 * computes the diff between the current ticked state and the existing
 * RoleMenu rows on the server, then issues a single bulk-create for
 * additions + per-row deletes for removals (the live API has no bulk
 * delete endpoint).
 *
 * Pages that want their menu visibility driven by this matrix should
 * call `useDynamicMenu()` (see `apps/panel/src/hooks/useDynamicMenu.ts`).
 */
export default function RoleMenuPermissions() {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [roles, setRoles] = useState<Role[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [joins, setJoins] = useState<RoleMenu[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permJoins, setPermJoins] = useState<RolePermission[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pickedPerms, setPickedPerms] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, m, j, p, rp] = await Promise.all([
        roleService.all().catch(() => [] as Role[]),
        menuService.all().catch(() => [] as Menu[]),
        roleMenuService.all().catch(() => [] as RoleMenu[]),
        permissionService.all().catch(() => [] as Permission[]),
        rolePermissionService.all().catch(() => [] as RolePermission[]),
      ]);
      setRoles(r ?? []);
      setMenus(m ?? []);
      setJoins(j ?? []);
      setPermissions(p ?? []);
      setPermJoins(rp ?? []);
      // Pre-expand parents on first load so the matrix is usable
      // without manual clicking.
      const parents = new Set<string>();
      for (const node of m ?? []) {
        if (node.parentId) parents.add(node.parentId);
      }
      setExpanded(parents);
      // Default-select first role if none chosen.
      if (!selectedRoleId && r && r.length > 0) {
        setSelectedRoleId(r[0]!.roleId ?? '');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load matrix';
      setError(msg);
      toastRef.current.error('Load failed', msg);
    } finally {
      setLoading(false);
    }
  }, [selectedRoleId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Re-derive the picked sets whenever the selected role or join
  // lists change — the dropdown switches roles without forcing a
  // refetch. Two sets: menu-visibility and per-verb permissions.
  useEffect(() => {
    if (!selectedRoleId) {
      setPicked(new Set());
      setPickedPerms(new Set());
      return;
    }
    const nextMenus = new Set<string>();
    for (const j of joins) {
      if (j.isActive === false) continue;
      if (j.roleId === selectedRoleId && j.menuId) nextMenus.add(j.menuId);
    }
    setPicked(nextMenus);

    const nextPerms = new Set<string>();
    for (const rp of permJoins) {
      if (rp.isActive === false) continue;
      if (rp.roleId === selectedRoleId && rp.permissionId) nextPerms.add(rp.permissionId);
    }
    setPickedPerms(nextPerms);
  }, [selectedRoleId, joins, permJoins]);

  // Build a parent → children map for the tree render. Roots have
  // `parentId == null`.
  const tree = useMemo(() => {
    const byParent = new Map<string | null | undefined, Menu[]>();
    for (const m of menus) {
      if (m.isActive === false) continue;
      const arr = byParent.get(m.parentId ?? null) ?? [];
      arr.push(m);
      byParent.set(m.parentId ?? null, arr);
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => {
        const sa = a.sortOrder ?? 0;
        const sb = b.sortOrder ?? 0;
        if (sa !== sb) return sa - sb;
        return (a.name ?? '').localeCompare(b.name ?? '');
      });
    }
    return byParent;
  }, [menus]);

  // Snapshot of the currently-saved row ids for the selected role,
  // keyed by menuId. We need the roleMenuId to delete on uncheck.
  const savedRows = useMemo(() => {
    const m = new Map<string, RoleMenu>();
    for (const j of joins) {
      if (j.roleId === selectedRoleId && j.menuId) m.set(j.menuId, j);
    }
    return m;
  }, [joins, selectedRoleId]);

  // Same snapshot for the permission side — keyed by permissionId.
  const savedPermRows = useMemo(() => {
    const m = new Map<string, RolePermission>();
    for (const rp of permJoins) {
      if (rp.roleId === selectedRoleId && rp.permissionId) m.set(rp.permissionId, rp);
    }
    return m;
  }, [permJoins, selectedRoleId]);

  // Group permissions by the menuKey their code encodes (e.g.
  // `inventory.products.view` → `inventory.products`). The matrix
  // renders one row per menuKey; only ticked menus show up so the
  // editor stays focused on the intersection.
  const permsByMenuKey = useMemo(() => {
    const map = new Map<string, Permission[]>();
    for (const p of permissions) {
      const c = (p.code ?? '').toString();
      const dot = c.lastIndexOf('.');
      const key = dot > 0 ? c.slice(0, dot) : c;
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return map;
  }, [permissions]);

  const menuKeyById = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of menus) {
      const key = (row as Menu & { menuKey?: string }).menuKey;
      if (row.menuId && key) m.set(row.menuId, key);
    }
    return m;
  }, [menus]);

  function toggle(menuId: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(menuId)) next.delete(menuId);
      else next.add(menuId);
      return next;
    });
  }

  function togglePerm(permId: string) {
    setPickedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  }

  function toggleExpand(menuId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(menuId)) next.delete(menuId);
      else next.add(menuId);
      return next;
    });
  }

  function selectAll() {
    const all = new Set<string>();
    for (const m of menus) {
      if (m.isActive !== false && m.menuId) all.add(m.menuId);
    }
    setPicked(all);
  }

  function clearAll() {
    setPicked(new Set());
  }

  /**
   * One-click: find the Administrator role (system role named
   * "Administrator" / "Admin"), grant it every active menu in the
   * tenant. Diffs against the live grants so re-runs are idempotent
   * — only ungranted menus are POSTed.
   *
   * NB: this assigns MENU visibility only. Role-level PERMISSION
   * grants (`RolePermission`) are seeded by the backend M2 migration
   * (`POST /api/Migration/Run`, see BACKEND_AGENT_PROMPTS.md D1).
   * The backend brief states "Administrator RoleMenu / RolePermission
   * grants always re-sync to the cartesian set" on every migration
   * run, so triggering D1 covers both surfaces in one shot.
   *
   * Bug ref: AI Agent 2026-06-11 — "assign all role admin all menus
   * permission".
   */
  async function grantAdminAllMenus() {
    // Prefer the system "Administrator" role; fall back to any role
    // whose name is Administrator/Admin.
    const adminRole =
      roles.find((r) => {
        const n = (r.name ?? '').toLowerCase();
        return r.isSystem && (n === 'administrator' || n === 'admin');
      }) ??
      roles.find((r) => {
        const n = (r.name ?? '').toLowerCase();
        return n === 'administrator' || n === 'admin';
      });

    if (!adminRole?.roleId) {
      toast.error(
        'Admin role not found',
        'No role named "Administrator" exists in this tenant. Run the D1 backend migration to seed it.',
      );
      return;
    }

    // Surface the chosen role in the dropdown so the user sees what was modified.
    setSelectedRoleId(adminRole.roleId);

    const activeMenuIds = menus
      .filter((m) => m.isActive !== false && m.menuId)
      .map((m) => m.menuId!);

    const currentAdminGrants = new Set(
      joins
        .filter((j) => j.roleId === adminRole.roleId && j.menuId && j.isActive !== false)
        .map((j) => j.menuId!),
    );

    const toAdd: RoleMenuCreate[] = activeMenuIds
      .filter((mid) => !currentAdminGrants.has(mid))
      .map((mid) => ({ roleId: adminRole.roleId!, menuId: mid, isActive: true }));

    if (toAdd.length === 0) {
      toast.info(
        'Already granted',
        `${adminRole.name} already has access to all ${activeMenuIds.length} active menus.`,
      );
      return;
    }

    setSaving(true);
    try {
      await roleMenuService.create(toAdd);
      toast.success(
        'Admin granted',
        `${adminRole.name}: +${toAdd.length} menus (total ${activeMenuIds.length})`,
      );
      refreshDynamicMenu();
      await reload();
    } catch (e) {
      toast.error('Grant failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    if (!selectedRoleId) {
      toast.error('Pick a role first');
      return;
    }
    setSaving(true);
    try {
      // Diff against the saved snapshot — menus.
      const menuToAdd: RoleMenuCreate[] = [];
      const menuToDelete: string[] = [];

      for (const menuId of picked) {
        if (!savedRows.has(menuId)) {
          menuToAdd.push({ roleId: selectedRoleId, menuId, isActive: true });
        }
      }
      for (const [menuId, row] of savedRows) {
        if (!picked.has(menuId) && row.roleMenuId) {
          menuToDelete.push(row.roleMenuId);
        }
      }

      // Same diff — permissions.
      const permToAdd: RolePermissionCreate[] = [];
      const permToDelete: string[] = [];

      for (const permId of pickedPerms) {
        if (!savedPermRows.has(permId)) {
          permToAdd.push({ roleId: selectedRoleId, permissionId: permId, isActive: true });
        }
      }
      for (const [permId, row] of savedPermRows) {
        if (!pickedPerms.has(permId) && row.rolePermissionId) {
          permToDelete.push(row.rolePermissionId);
        }
      }

      const totalChanges = menuToAdd.length + menuToDelete.length + permToAdd.length + permToDelete.length;
      if (totalChanges === 0) {
        toast.info('No changes', 'The matrix already matches the server.');
        return;
      }

      if (menuToAdd.length > 0) await roleMenuService.create(menuToAdd);
      // Live API has no bulk delete — issue per-row deletes. They run
      // serially so a 500 stops the loop and the user can retry.
      for (const id of menuToDelete) await roleMenuService.delete(id);

      if (permToAdd.length > 0) await rolePermissionService.create(permToAdd);
      for (const id of permToDelete) await rolePermissionService.delete(id);

      toast.success(
        'Role updated',
        `Menus: +${menuToAdd.length} / -${menuToDelete.length}  ·  Perms: +${permToAdd.length} / -${permToDelete.length}`,
      );
      // Reset the dynamic-menu cache so the sidebar reflects the change
      // on the next route navigation.
      refreshDynamicMenu();
      // Refetch to pick up the new roleMenuIds / rolePermissionIds.
      await reload();
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  const roleName = roles.find((r) => r.roleId === selectedRoleId)?.name ?? '';
  const dirty = (() => {
    if (!selectedRoleId) return false;
    if (picked.size !== savedRows.size) return true;
    for (const k of picked) if (!savedRows.has(k)) return true;
    if (pickedPerms.size !== savedPermRows.size) return true;
    for (const k of pickedPerms) if (!savedPermRows.has(k)) return true;
    return false;
  })();

  // Matrix scope — the permissions table only shows rows for menus
  // the role currently has access to. That keeps the picker composable:
  // menu ticked = row visible; verb ticks = what the role can do.
  const VERBS = ['create', 'view', 'update', 'delete', 'download', 'import', 'export'] as const;
  type Verb = (typeof VERBS)[number];
  const pickedMenuKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const id of picked) {
      const k = menuKeyById.get(id);
      if (k) keys.add(k);
    }
    return keys;
  }, [picked, menuKeyById]);

  const menuByKey = useMemo(() => {
    const map = new Map<string, Menu>();
    for (const m of menus) {
      const k = (m as Menu & { menuKey?: string }).menuKey;
      if (k) map.set(k, m);
    }
    return map;
  }, [menus]);

  function verbOf(p: Permission): string {
    const c = (p.code ?? '').toString();
    const dot = c.lastIndexOf('.');
    return (dot > 0 ? c.slice(dot + 1) : (p.action ?? '')).toLowerCase();
  }

  function toggleRowPerms(menuKey: string, checked: boolean) {
    const rowPerms = permsByMenuKey.get(menuKey) ?? [];
    setPickedPerms((prev) => {
      const next = new Set(prev);
      for (const p of rowPerms) {
        if (!p.permissionId) continue;
        if (checked) next.add(p.permissionId);
        else next.delete(p.permissionId);
      }
      return next;
    });
  }

  function toggleVerbColumn(verb: Verb, checked: boolean) {
    setPickedPerms((prev) => {
      const next = new Set(prev);
      for (const p of permissions) {
        if (verbOf(p) !== verb) continue;
        if (!p.permissionId) continue;
        // Only touch permissions that belong to menus this role
        // actually sees — otherwise "toggle all view" would grant
        // hidden menus by side-effect.
        const c = (p.code ?? '').toString();
        const dot = c.lastIndexOf('.');
        const key = dot > 0 ? c.slice(0, dot) : c;
        if (pickedMenuKeys.size > 0 && !pickedMenuKeys.has(key)) continue;
        if (checked) next.add(p.permissionId);
        else next.delete(p.permissionId);
      }
      return next;
    });
  }

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="text-base font-semibold text-content-primary">Role-Menu permissions</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={reload} disabled={loading || saving}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {/* One-click: assign every active menu to the Administrator
                role. Idempotent — only the ungranted menus are POSTed.
                Doesn't touch the matrix's `picked` set so the user can
                continue editing whichever role they had open. */}
            <Button
              variant="outline"
              size="sm"
              onClick={grantAdminAllMenus}
              disabled={loading || saving || roles.length === 0}
              title="Grant the Administrator role access to every active menu"
            >
              <ShieldPlus className="h-3.5 w-3.5" /> Grant Admin all menus
            </Button>
            <Button variant="outline" size="sm" onClick={selectAll} disabled={!selectedRoleId || saving}>
              <CheckSquare className="h-3.5 w-3.5" /> Select all
            </Button>
            <Button variant="outline" size="sm" onClick={clearAll} disabled={!selectedRoleId || saving}>
              <Square className="h-3.5 w-3.5" /> Clear
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!dirty || saving || !selectedRoleId}>
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>

        {/* Composite editor — menu visibility above, per-verb
            permissions below. Save writes both diffs in one round
            of adds/deletes. The seeder still stamps the tenant's
            initial state via `POST /api/Migration/Run` (D1) but
            everything on this page is editable afterwards. */}
        <Alert variant="info" className="mt-3">
          <span className="text-xs">
            Menu visibility <strong>and</strong> per-verb permissions are edited
            together — save writes RoleMenu + RolePermission diffs in one shot.
            The seeder still stamps the tenant's initial state (run
            <code className="mx-1 rounded bg-surface-card-hover px-1 font-mono">POST /api/Migration/Run</code>
            once per deploy).
          </span>
        </Alert>

        <div className="grid gap-3 py-3 sm:grid-cols-[260px_1fr] sm:items-end">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Role</span>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="h-10 rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary"
              disabled={loading || saving}
            >
              <option value="">— Select a role —</option>
              {roles.map((r) => (
                <option key={r.roleId} value={r.roleId ?? ''}>
                  {r.name}
                  {r.isSystem ? ' (system)' : ''}
                </option>
              ))}
            </select>
          </label>
          {selectedRoleId && (
            <p className="text-xs text-content-muted">
              {picked.size} of {menus.filter((m) => m.isActive !== false).length} menus visible to{' '}
              <span className="font-semibold text-content-primary">{roleName}</span>.
              {dirty && <span className="ml-2 rounded bg-warning-light px-1.5 py-0.5 text-warning">unsaved</span>}
            </p>
          )}
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-content-muted">Loading…</div>
        ) : !selectedRoleId ? (
          <div className="py-12 text-center text-sm text-content-muted">
            Select a role above to see and edit its menu access.
          </div>
        ) : menus.length === 0 ? (
          <div className="py-12 text-center text-sm text-content-muted">
            No menus seeded yet. Create entries under <code className="rounded bg-surface-card-hover px-1">/panel/menus</code> first.
          </div>
        ) : (
          <>
            <div className="mb-2 text-xs font-semibold uppercase text-content-secondary">Menu visibility</div>
            <div className="rounded-md border border-border bg-surface-card-hover">
              <Tree
                tree={tree}
                parentId={null}
                picked={picked}
                expanded={expanded}
                depth={0}
                onToggle={toggle}
                onToggleExpand={toggleExpand}
              />
            </div>

            <div className="mt-6 mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase text-content-secondary">
                Permissions ({pickedPerms.size} granted)
              </div>
              {pickedMenuKeys.size === 0 && (
                <span className="text-xs text-warning">Pick menus above to enable the matrix.</span>
              )}
            </div>
            <div className="max-h-[520px] overflow-auto rounded-md border border-border bg-surface-card-hover">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-surface-card">
                  <tr className="text-left text-xs font-semibold uppercase text-content-secondary">
                    <th className="px-3 py-2">Menu</th>
                    {VERBS.map((v) => (
                      <th key={v} className="px-2 py-2 text-center capitalize">
                        <div className="flex flex-col items-center gap-1">
                          <span>{v}</span>
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 cursor-pointer rounded border-border text-primary focus:ring-primary"
                            title={`Toggle all ${v} (for visible menus)`}
                            onChange={(e) => toggleVerbColumn(v, e.target.checked)}
                          />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {Array.from(pickedMenuKeys).length === 0 && (
                    <tr>
                      <td colSpan={VERBS.length + 1} className="px-3 py-6 text-center text-content-muted">
                        Tick at least one menu to edit its permissions.
                      </td>
                    </tr>
                  )}
                  {Array.from(pickedMenuKeys)
                    .sort((a, b) => a.localeCompare(b))
                    .map((mk) => {
                      const rowPerms = permsByMenuKey.get(mk) ?? [];
                      const rowMenu = menuByKey.get(mk);
                      const rowAllOn =
                        rowPerms.length > 0 &&
                        rowPerms.every((p) => p.permissionId && pickedPerms.has(p.permissionId));
                      return (
                        <tr key={mk} className="hover:bg-surface-card">
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={rowAllOn}
                                onChange={(e) => toggleRowPerms(mk, e.target.checked)}
                                className="h-3.5 w-3.5 cursor-pointer rounded border-border text-primary focus:ring-primary"
                                title="Toggle every verb for this menu"
                              />
                              <div className="flex flex-col">
                                <span className="font-medium text-content-primary">{rowMenu?.name ?? mk}</span>
                                <span className="font-mono text-[10px] text-content-muted">{mk}</span>
                              </div>
                            </div>
                          </td>
                          {VERBS.map((v) => {
                            const p = rowPerms.find((x) => verbOf(x) === v);
                            if (!p?.permissionId) {
                              return <td key={v} className="px-2 py-2 text-center text-content-muted">—</td>;
                            }
                            const on = pickedPerms.has(p.permissionId);
                            return (
                              <td key={v} className="px-2 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => togglePerm(p.permissionId!)}
                                  className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Tree renderer — recursive so nested menu hierarchies show indented.       */
/* ------------------------------------------------------------------------ */

function Tree({
  tree,
  parentId,
  picked,
  expanded,
  depth,
  onToggle,
  onToggleExpand,
}: {
  tree: Map<string | null | undefined, Menu[]>;
  parentId: string | null;
  picked: Set<string>;
  expanded: Set<string>;
  depth: number;
  onToggle: (id: string) => void;
  onToggleExpand: (id: string) => void;
}) {
  const nodes = tree.get(parentId) ?? [];
  if (nodes.length === 0) return null;
  return (
    <ul className={depth === 0 ? 'divide-y divide-border' : ''}>
      {nodes.map((m) => {
        const id = m.menuId!;
        const kids = tree.get(id) ?? [];
        const hasKids = kids.length > 0;
        const isOpen = expanded.has(id);
        const checked = picked.has(id);
        return (
          <li key={id}>
            <div
              className="flex items-center gap-2 px-3 py-2 hover:bg-surface-card"
              style={{ paddingLeft: `${12 + depth * 20}px` }}
            >
              <button
                type="button"
                onClick={() => hasKids && onToggleExpand(id)}
                className="inline-flex h-5 w-5 items-center justify-center rounded text-content-muted hover:text-content-primary disabled:opacity-30"
                disabled={!hasKids}
                aria-label={hasKids ? (isOpen ? 'Collapse' : 'Expand') : 'Leaf'}
              >
                {hasKids ? (isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : <span className="block h-1 w-1 rounded-full bg-border" />}
              </button>
              <label className="flex flex-1 cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(id)}
                  className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                />
                <span className="font-medium text-content-primary">{m.name}</span>
                {m.route && (
                  <span className="text-xs text-content-muted">{m.route}</span>
                )}
              </label>
            </div>
            {hasKids && isOpen && (
              <Tree
                tree={tree}
                parentId={id}
                picked={picked}
                expanded={expanded}
                depth={depth + 1}
                onToggle={onToggle}
                onToggleExpand={onToggleExpand}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
